import { Component, createRef } from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import { Alert, Button } from "react-bootstrap";

import { checkCookie, getCookie } from "../../Utils/Utils";
import Header from "../../Content/Header/Header";
import {
  ENABLE_RESONATE_WEBSITE,
  SUBSCRIPTION_AMOUNT,
  SUBSCRIPTION_API,
  SUBSCRIPTION_TOTAL_API,
} from "../../../constants/Constants";

require("./Subscription.scss");

const DEFAULT_AMOUNT = 100;

const GIVING_MODES = {
  MONTHLY: "monthly",
  ONE_TIME: "one-time",
};

const AMOUNT_TYPES = {
  PRESET: "preset",
  OTHER: "other",
};

const SUBSCRIPTION_AMOUNTS = [...new Set(Object.values(SUBSCRIPTION_AMOUNT).map(Number))].sort(
  (a, b) => a - b
);

class Subscription extends Component {
  constructor(props) {
    super(props);

    this.customInputRef = createRef();

    this.state = {
      redirect: false,
      isLoading: false,
      isSubmitting: false,
      donationMode: GIVING_MODES.MONTHLY,
      amountType: AMOUNT_TYPES.PRESET,
      subscription: {
        userId: "",
        subscriptionAmount: DEFAULT_AMOUNT,
        donationMode: GIVING_MODES.MONTHLY,
      },
      customAmount: "",
      totalSubscriptionAmount: 0,
      totalSubscriptionCount: 0,
      subscription25Count: 0,
      subscription50Count: 0,
      subscription75Count: 0,
      subscription100Count: 0,
      subscription250Count: 0,
      subscription500Count: 0,
      subscription1000Count: 0,
      subscriptionOtherCount: 0,
      totalOneTimeCommitmentCount: 0,
      totalOneTimeCommitmentAmount: 0,
      userSubscribed: false,
      userOneTimeCommitment: false,
      subscriptionMessage: "",
      submissionStatusMessage: "",
      submissionStatusVariant: "success",
      showOverridePrompt: false,
      pendingPayload: null,
    };

    this.handleSubscriptionChange = this.handleSubscriptionChange.bind(this);
    this.handleSubscriptionSubmit = this.handleSubscriptionSubmit.bind(this);
    this.handleOverrideSubmit = this.handleOverrideSubmit.bind(this);
    this.handleDonationModeChange = this.handleDonationModeChange.bind(this);
    this.handleAmountTypeChange = this.handleAmountTypeChange.bind(this);
    this.handleCustomAmountChange = this.handleCustomAmountChange.bind(this);
  }

  componentDidMount() {
    if (!checkCookie("emailId") || !ENABLE_RESONATE_WEBSITE) {
      this.setState({ redirect: true });
      return;
    }

    const userId = getCookie("userId");
    const subscription = {
      ...this.state.subscription,
      userId,
      subscriptionAmount: DEFAULT_AMOUNT,
      donationMode: GIVING_MODES.MONTHLY,
    };

    this.setState(
      {
        isLoading: true,
        subscription,
        subscriptionMessage: this.getImpactMessage(DEFAULT_AMOUNT, GIVING_MODES.MONTHLY),
      },
      () => this.fetchTotals(userId)
    );
  }

  getImpactMessage = (amount, donationMode) => {
    if (!amount || amount <= 0) {
      return "";
    }

    if (donationMode === GIVING_MODES.ONE_TIME) {
      return `Missions receive 🎁 $${amount}`;
    }

    return `Missions receive 🎁 $${amount * 12} a year`;
  };

  getButtonLabel = (donationMode) => {
    return donationMode === GIVING_MODES.ONE_TIME ? "Give Now" : "Start Your Monthly Impact";
  };

  fetchTotals = (userId) => {
    axios
      .get(`${SUBSCRIPTION_TOTAL_API}/${userId}`)
      .then((response) => {
        const data = response.data || {};

        this.setState({
          totalSubscriptionAmount: data.totalAmount || 0,
          totalSubscriptionCount: data.totalRecurringCommitmentCount || 0,
          subscription25Count: data.dollar25SubscriptionCount || 0,
          subscription50Count: data.dollar50SubscriptionCount || 0,
          subscription75Count: data.dollar75SubscriptionCount || 0,
          subscription100Count: data.dollar100SubscriptionCount || 0,
          subscription250Count: data.dollar250SubscriptionCount || 0,
          subscription500Count: data.dollar500SubscriptionCount || 0,
          subscription1000Count: data.dollar1000SubscriptionCount || 0,
          subscriptionOtherCount: data.dollarOtherSubscriptionCount || 0,
          totalOneTimeCommitmentCount: data.totalOneTimeCommitmentCount || 0,
          totalOneTimeCommitmentAmount: data.totalOneTimeCommitmentAmount || 0,
          userSubscribed: data.userSubscribed || false,
          userOneTimeCommitment: data.userOneTimeCommitment || false,
          isLoading: false,
        });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  };

  handleDonationModeChange(donationMode) {
    const { subscription, amountType, customAmount } = this.state;

    let updatedAmount = subscription.subscriptionAmount;

    if (amountType === AMOUNT_TYPES.OTHER) {
      const parsedCustomAmount = Number(customAmount);
      updatedAmount =
        Number.isFinite(parsedCustomAmount) && parsedCustomAmount > 0 ? parsedCustomAmount : 0;
    }

    const updatedSubscription = {
      ...subscription,
      donationMode,
      subscriptionAmount: updatedAmount,
    };

    this.setState({
      donationMode,
      subscription: updatedSubscription,
      subscriptionMessage: this.getImpactMessage(updatedAmount, donationMode),
      submissionStatusMessage: "",
      submissionStatusVariant: "success",
      showOverridePrompt: false,
      pendingPayload: null,
    });
  }

  handleAmountTypeChange(type) {
    const { donationMode, subscription, customAmount } = this.state;

    if (type === AMOUNT_TYPES.OTHER) {
      const parsedCustomAmount = Number(customAmount);
      const updatedAmount =
        Number.isFinite(parsedCustomAmount) && parsedCustomAmount > 0 ? parsedCustomAmount : 0;

      this.setState(
        {
          amountType: type,
          subscription: {
            ...subscription,
            subscriptionAmount: updatedAmount,
          },
          subscriptionMessage: this.getImpactMessage(updatedAmount, donationMode),
          submissionStatusMessage: "",
          submissionStatusVariant: "success",
          showOverridePrompt: false,
          pendingPayload: null,
        },
        () => {
          if (this.customInputRef.current) {
            this.customInputRef.current.focus();
            this.customInputRef.current.select();
          }
        }
      );
      return;
    }

    this.setState({
      amountType: type,
      customAmount: "",
      subscription: {
        ...subscription,
        subscriptionAmount: DEFAULT_AMOUNT,
      },
      subscriptionMessage: this.getImpactMessage(DEFAULT_AMOUNT, donationMode),
      submissionStatusMessage: "",
      submissionStatusVariant: "success",
      showOverridePrompt: false,
      pendingPayload: null,
    });
  }

  handleCustomAmountChange(e) {
    const value = e.target.value;

    if (!/^\d*$/.test(value)) {
      return;
    }

    const amount = value === "" ? 0 : Number(value);
    const { donationMode, subscription } = this.state;

    this.setState({
      customAmount: value,
      subscription: {
        ...subscription,
        subscriptionAmount: amount,
      },
      subscriptionMessage: this.getImpactMessage(amount, donationMode),
      submissionStatusMessage: "",
      submissionStatusVariant: "success",
      showOverridePrompt: false,
      pendingPayload: null,
    });
  }

  handleSubscriptionChange(e) {
    let subscriptionAmount = 0;

    if (e && e.target) {
      subscriptionAmount = Number(e.target.value);
    } else {
      subscriptionAmount = Number(e);
    }

    const { donationMode, subscription } = this.state;
    const updatedSubscription = {
      ...subscription,
      subscriptionAmount,
    };

    this.setState({
      amountType: AMOUNT_TYPES.PRESET,
      customAmount: "",
      subscription: updatedSubscription,
      subscriptionMessage: this.getImpactMessage(subscriptionAmount, donationMode),
      submissionStatusMessage: "",
      submissionStatusVariant: "success",
      showOverridePrompt: false,
      pendingPayload: null,
    });
  }

  handleSubscriptionSubmit() {
    const { subscription, donationMode, userSubscribed, userOneTimeCommitment } = this.state;

    if (
      !subscription.userId ||
      !subscription.subscriptionAmount ||
      subscription.subscriptionAmount <= 0
    ) {
      return;
    }

    const isMonthly = donationMode === GIVING_MODES.MONTHLY;
    const alreadyExists = isMonthly ? userSubscribed : userOneTimeCommitment;

    const payload = {
      ...subscription,
      recurring: isMonthly,
      overrideExisting: false,
    };

    if (alreadyExists) {
      this.setState({
        isSubmitting: false,
        submissionStatusMessage: isMonthly
          ? "You already have a monthly subscription. Click Override Existing Gift to replace it."
          : "You already have a one-time payment. Click Override Existing Gift to replace it.",
        submissionStatusVariant: "warning",
        showOverridePrompt: true,
        pendingPayload: payload,
      });
      return;
    }

    this.setState({
      isSubmitting: true,
      submissionStatusMessage: "",
      submissionStatusVariant: "success",
      showOverridePrompt: false,
      pendingPayload: null,
    });

    axios
      .post(SUBSCRIPTION_API, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        this.setState(
          {
            subscription: {
              ...subscription,
              donationMode,
            },
            subscriptionMessage: "",
            submissionStatusMessage:
              donationMode === GIVING_MODES.ONE_TIME
                ? "Your one-time gift was submitted successfully. Thank you for your support!"
                : "Your monthly commitment was submitted successfully. Thank you for supporting the mission!",
            submissionStatusVariant: "success",
            isSubmitting: false,
            showOverridePrompt: false,
            pendingPayload: null,
          },
          () => this.fetchTotals(subscription.userId)
        );
      })
      .catch(() => {
        this.setState({
          isSubmitting: false,
          submissionStatusMessage:
            "Something went wrong while submitting your gift. Please try again.",
          submissionStatusVariant: "danger",
        });
      });
  }

  handleOverrideSubmit() {
    const { pendingPayload, subscription, donationMode } = this.state;

    if (!pendingPayload) {
      return;
    }

    this.setState({ isSubmitting: true });

    const overridePayload = {
      ...pendingPayload,
      overrideExisting: true,
    };

    axios
      .post(SUBSCRIPTION_API, overridePayload, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        this.setState(
          {
            subscription: {
              ...subscription,
              donationMode,
            },
            subscriptionMessage: "",
            submissionStatusMessage:
              donationMode === GIVING_MODES.ONE_TIME
                ? "Your one-time gift was overridden successfully."
                : "Your monthly subscription was overridden successfully.",
            submissionStatusVariant: "success",
            isSubmitting: false,
            showOverridePrompt: false,
            pendingPayload: null,
          },
          () => this.fetchTotals(subscription.userId)
        );
      })
      .catch(() => {
        this.setState({
          isSubmitting: false,
          submissionStatusMessage:
            "Unable to override the existing gift right now. Please try again.",
          submissionStatusVariant: "danger",
        });
      });
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to="/" />;
    }
    const {
      subscriptionMessage,
      subscription,
      totalSubscriptionAmount,
      totalSubscriptionCount,
      subscription25Count,
      subscription50Count,
      subscription75Count,
      subscription100Count,
      subscription250Count,
      subscription500Count,
      subscription1000Count,
      subscriptionOtherCount,
      isLoading,
      isSubmitting,
      donationMode,
      amountType,
      totalOneTimeCommitmentCount,
      customAmount,
      submissionStatusMessage,
      submissionStatusVariant,
      showOverridePrompt,
    } = this.state;

    const subscriptionStats = [
      { key: "$25", count: subscription25Count, type: "gift" },
      { key: "$50", count: subscription50Count, type: "gift" },
      { key: "$75", count: subscription75Count, type: "gift" },
      { key: "$100", count: subscription100Count, type: "gift" },
      { key: "$250", count: subscription250Count, type: "gift" },
      { key: "$500", count: subscription500Count, type: "gift" },
      { key: "$1000", count: subscription1000Count, type: "gift" },
      { key: "Other", count: subscriptionOtherCount, type: "gift" },
    ];

    const buttonLabel = this.getButtonLabel(donationMode);
    const isCustomSelected = amountType === AMOUNT_TYPES.OTHER;

    return (
      <div className="subscription-page-container subscription-page-container--redesign">
        <Header />

        <div className="subscription-hero">
          <div className="subscription-kicker">Be part of our expanding community!</div>
          <div className="subscription-title">Give once or monthly to create lasting impact.</div>
        </div>

        <div className="subscription-social-proof">
          With <strong> {totalSubscriptionCount} </strong> new supporters joining, our growing community has now pledged <strong>${totalSubscriptionAmount}</strong> annually to support our mission.
        </div>

        <div className="subscription-card">
          <div className="subscription-toggle" role="tablist" aria-label="Giving frequency">
            <button
              type="button"
              className={`subscription-toggle-option ${
                donationMode === GIVING_MODES.ONE_TIME ? "is-active" : ""
              }`}
              onClick={() => this.handleDonationModeChange(GIVING_MODES.ONE_TIME)}
              aria-pressed={donationMode === GIVING_MODES.ONE_TIME}
            >
              One-time
            </button>

            <button
              type="button"
              className={`subscription-toggle-option ${
                donationMode === GIVING_MODES.MONTHLY ? "is-active" : ""
              }`}
              onClick={() => this.handleDonationModeChange(GIVING_MODES.MONTHLY)}
              aria-pressed={donationMode === GIVING_MODES.MONTHLY}
            >
              Monthly
            </button>
          </div>

          <div className="subscription-option subscription-option--grid">
            {SUBSCRIPTION_AMOUNTS.map((amount) => {
              const isSelected =
                amountType === AMOUNT_TYPES.PRESET && subscription.subscriptionAmount === amount;
              const isPopular = amount === DEFAULT_AMOUNT;

              return (
                <Button
                  className={`subscription-option-value ${isSelected ? "is-selected" : ""}`}
                  key={amount}
                  variant="light"
                  onClick={() => this.handleSubscriptionChange(amount)}
                  aria-pressed={isSelected}
                >
                  <span className="subscription-option-amount">${amount}</span>
                  {donationMode === GIVING_MODES.MONTHLY && (
                    <span className="subscription-monthly-label"></span>
                  )}
                  {isPopular && <span className="subscription-badge">Most Popular</span>}
                </Button>
              );
            })}

            <div
              className={`subscription-option-value subscription-option-custom ${
                isCustomSelected ? "is-selected" : ""
              }`}
              role="button"
              tabIndex={0}
              onClick={() => this.handleAmountTypeChange(AMOUNT_TYPES.OTHER)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  this.handleAmountTypeChange(AMOUNT_TYPES.OTHER);
                }
              }}
              aria-pressed={isCustomSelected}
            >
              <input
                ref={this.customInputRef}
                type="text"
                name="customAmount"
                id="customAmount"
                aria-label="Custom donation amount"
                inputMode="numeric"
                pattern="[0-9]*"
                className="subscription-inline-input"
                placeholder="Other amount"
                value={isCustomSelected ? customAmount : ""}
                onClick={() => this.handleAmountTypeChange(AMOUNT_TYPES.OTHER)}
                onFocus={() => this.handleAmountTypeChange(AMOUNT_TYPES.OTHER)}
                onChange={this.handleCustomAmountChange}
              />
            </div>
          </div>

          <div className="subscription-impact-box">
            <div className="subscription-message">{subscriptionMessage}</div>
          </div>

          {submissionStatusMessage && (
            <Alert variant={submissionStatusVariant} className="subscription-status-message">
              {submissionStatusMessage}
            </Alert>
          )}

          {showOverridePrompt ? (
            <Button
              className="subscription-button subscription-button--override"
              onClick={this.handleOverrideSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Overriding..." : "Override Existing Gift"}
            </Button>
          ) : (
            <Button
              className="subscription-button"
              onClick={this.handleSubscriptionSubmit}
              disabled={isSubmitting || !subscription.subscriptionAmount}
            >
              {isSubmitting ? "Submitting..." : buttonLabel}
            </Button>
          )}
        </div>

        <div className="subscription-details">
          <div className="subscription-summary">
            <div className="subscription-stats">
              {subscriptionStats
                .filter((item) => isLoading || item.count > 0)
                .map((item) => (
                  <span key={item.key}>
                    <strong>{item.key} Gifts: </strong> {isLoading ? "..." : item.count}
                  </span>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Subscription;